import * as React from "react";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import {
  CustomFormField,
  FieldErrors,
  FieldType,
  DROPDOWN_FIELD_TYPE_ID,
} from "./customField.types";

interface Props {
  open: boolean;
  singleField: CustomFormField;
  setSingleField: React.Dispatch<React.SetStateAction<any>>;
  dropdownField: FieldType[];
  options: string[];
  addOption: () => void;
  removeOption: () => void;
  handleChange: (index: number, value: string) => void;
  errors: FieldErrors;
  onRequiredChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onUniqueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setOptions: React.Dispatch<React.SetStateAction<string[]>>;
  onClose: () => void;
  onCancel: () => void;
  onSave: () => void;
}

export default function CustomFieldDrawer({
  open,
  singleField,
  setSingleField,
  dropdownField,
  options,
  addOption,
  removeOption,
  handleChange,
  errors,
  onRequiredChange,
  onUniqueChange,
  setOptions,
  onClose,
  onCancel,
  onSave,
}: Props) {
  const isEdit = !!singleField._id;

  return (
    <>
      <div className={`ctpl-overlay${open ? " open" : ""}`} onClick={onClose} />
      <div className={`ctpl-drawer ctpl-drawer-wide${open ? " open" : ""}`}>
        <div className="ctpl-drawer-header">
          <h2>{isEdit ? "Field Properties" : "Add Field"}</h2>
          <button className="ctpl-icon-btn" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div className="ctpl-drawer-body">
          <p className="ctpl-hint" style={{ marginTop: -6, marginBottom: 18 }}>
            Configure the selected field properties and validation rules.
          </p>

          {/* Field name */}
          <div className="ctpl-field-group">
            <label className="ctpl-field-label">
              Field Name <span className="ctpl-required">*</span>
            </label>
            <input
              type="text"
              className={`ctpl-field-input${errors.fieldName ? " error" : ""}`}
              value={singleField.fieldName || ""}
              placeholder="Enter field name"
              onChange={(event) =>
                setSingleField((prev: any) => ({ ...prev, fieldName: event.target.value }))
              }
            />
            {errors.fieldName && <div className="ctpl-field-error">{errors.fieldName}</div>}
          </div>

          {/* Field type */}
          <div className="ctpl-field-group">
            <label className="ctpl-field-label">
              Field Type <span className="ctpl-required">*</span>
            </label>
            <select
              className={`ctpl-field-input${errors.fieldType ? " error" : ""}`}
              value={singleField.fieldType || ""}
              onChange={(event) => {
                setSingleField((prev: any) => ({
                  ...prev,
                  fieldType: event.target.value,
                }));
                if (event.target.value == DROPDOWN_FIELD_TYPE_ID) {
                  return;
                } else {
                  setOptions([]);
                }
              }}
            >
              <option value="" disabled hidden>
                Select field type
              </option>
              {dropdownField.map((field: any) => (
                <option key={field._id} value={field._id}>
                  {field.name}
                </option>
              ))}
            </select>
            {errors.fieldType && <div className="ctpl-field-error">{errors.fieldType}</div>}
          </div>

          {/* Description */}
          <div className="ctpl-field-group">
            <label className="ctpl-field-label">
              Description <span className="ctpl-required">*</span>
            </label>
            <input
              type="text"
              className={`ctpl-field-input${errors.description ? " error" : ""}`}
              value={singleField.description || ""}
              placeholder="Help text for users"
              onChange={(event) =>
                setSingleField((prev: any) => ({ ...prev, description: event.target.value }))
              }
            />
            <div className="ctpl-hint">Max 200 characters</div>
            {errors.description && <div className="ctpl-field-error">{errors.description}</div>}
          </div>

          {/* Default value */}
          <div className="ctpl-field-group">
            <label className="ctpl-field-label">
              Default Value <span className="ctpl-optional">(optional)</span>
            </label>
            <input
              type="text"
              className="ctpl-field-input"
              value={singleField.defaultValue || ""}
              placeholder="Enter default value"
              onChange={(event) =>
                setSingleField((prev: any) => ({ ...prev, defaultValue: event.target.value }))
              }
            />
          </div>

          {/* Required */}
          <div className="ctpl-field-group">
            <label className="cf-checkbox-row" htmlFor="mandatory">
              <input
                type="checkbox"
                id="mandatory"
                checked={singleField.required || false}
                onChange={onRequiredChange}
              />
              <span>Required Field</span>
            </label>
            <div className="ctpl-hint">
              Users must fill this field when entering tracking numbers
            </div>
          </div>

          {/* Unique */}
          <div className="ctpl-field-group">
            <label className="cf-checkbox-row" htmlFor="uniqueValues">
              <input
                type="checkbox"
                id="uniqueValues"
                checked={singleField.isUnique || false}
                onChange={onUniqueChange}
              />
              <span>Unique Values</span>
            </label>
            <div className="ctpl-hint">Each entry for this field must have a unique value</div>
          </div>

          {/* Dropdown options */}
          {singleField.fieldType === DROPDOWN_FIELD_TYPE_ID && (
            <div className="ctpl-field-group cf-options-box">
              <label className="ctpl-field-label">Dropdown Options</label>
              <div>
                {options &&
                  options.map((option, index) => (
                    <input
                      key={index}
                      type="text"
                      className="ctpl-field-input"
                      value={option}
                      onChange={(e) => handleChange(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      style={{ marginBottom: 8 }}
                    />
                  ))}
                {errors.options && <div className="ctpl-field-error">{errors.options}</div>}
              </div>
              <div className="cf-options-actions">
                <button type="button" className="cf-link-btn" onClick={addOption}>
                  <AddIcon fontSize="small" /> Add Option
                </button>
                <button type="button" className="cf-link-btn danger" onClick={removeOption}>
                  <DeleteOutlineIcon fontSize="small" /> Remove
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="ctpl-drawer-footer">
          <button className="ctpl-btn ctpl-btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button className="ctpl-btn ctpl-btn-primary" onClick={onSave}>
            {isEdit ? "Save changes" : "Save Field"}
          </button>
        </div>
      </div>
    </>
  );
}